import React, { ReactElement } from "react";
import { ObjectWithStringSignature } from "./componentTypes";
import { Plan2PlateInput, Plan2PlateInputProps } from "./Plan2PlateInput";

type NumericKeys<T> = keyof { [K in keyof T]: T[K] extends number ? K : never };

export type NumberInputProps<
  T extends ObjectWithStringSignature,
  K extends NumericKeys<T> & string,
> = Omit<
  Plan2PlateInputProps<T, K, number>,
  | "inputType"
  | "valueConverter"
  | "leftAddon"
  | "rightAddon"
  | "leftAddonLabel"
  | "rightAddonLabel"
> & {
  updater: (fieldname: K, value: number) => void;
  fieldDescription?: string;
  fieldUnit?: string;
  precision?: number;
  dataCy?: string;
};

const NumberInputInternal = <
  T extends ObjectWithStringSignature,
  K extends NumericKeys<T> & string,
>(
  props: NumberInputProps<T, K>,
  ref: React.ForwardedRef<HTMLInputElement>,
): ReactElement => {
  return (
    <Plan2PlateInput
      {...props}
      ref={ref}
      dataCy={props.dataCy}
      value={props.value}
      valueFormatter={(value) =>
        props.precision ? (Number(value).toFixed(2) as unknown as T[K]) : value
      }
      isInvalid={props.isInvalid || props.value < 0}
      inputType={"number"}
      valueConverter={(v) => Number(v)}
      leftAddonLabel={props.fieldDescription}
      rightAddonLabel={props.fieldUnit}
      placeholder={props.placeholder || props.fieldDescription}
    />
  );
};

export const NumberInput = React.forwardRef(NumberInputInternal) as <
  T extends ObjectWithStringSignature,
  K extends NumericKeys<T> & string,
>(
  props: NumberInputProps<T, K> & { ref?: React.Ref<HTMLInputElement> },
) => ReactElement;
